// src/components/leads/forms/EditLeadForm.tsx
import React, { type ReactNode, useState, useMemo } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Country } from "@/lib/locationCatalog";
import Select, { type SingleValue } from "react-select";

import {
  type EditLeadFormState,
  type LeadStatusUi,
  type LeadSourceUi,
  type PriorityUi,
  type TransferTimelineEntry,
  type VisaDetailsDraftState,
  TRANSFER_REASON_OPTIONS,
} from "@/pages/leads/useEditLeadPage";

import {
  FiClock,
  FiUser,
  FiArrowRight,
  FiTag,
  FiMail,
  FiCalendar,
  FiLayers,
} from "react-icons/fi";

/* --------------------------------- Types --------------------------------- */

type Option = { value: string; label: string };

type EditLeadFormProps = {
  // main form
  value: EditLeadFormState;
  onChange: (patch: Partial<EditLeadFormState>) => void;
  errorMessage?: string;
  submitting?: boolean;
  onSubmit: () => void;
  onCancel: () => void;

  // dropdown options
  productOptions: Option[];
  employeeOptions: Option[];
  statusOptions: LeadStatusUi[];
  sourceOptions: LeadSourceUi[];
  priorityOptions: PriorityUi[];

  // transfer section
  transferProductOptions: Option[];
  transferProductId: number | null;
  onChangeTransferProductId: (id: number | null) => void;

  transferToUserId: number | null;
  onChangeTransferToUserId: (id: number | null) => void;

  /** 🔹 NEW: target product for transfer */
  transferToProductId: number | null;
  onChangeTransferToProductId: (id: number | null) => void;

  transferReasonKey: string;
  onChangeTransferReasonKey: (key: string) => void;

  transferReasonText: string;
  onChangeTransferReasonText: (text: string) => void;

  onTransfer: () => void;
  transferring?: boolean;
  canManageTransfers?: boolean;
  isVisaDetailsSaving?: boolean;

  // visa details section
  visaLeadProductId: number | null;
  onChangeVisaLeadProductId: (id: number | null) => void;
  visaProductOptions: { value: number; label: string }[];
  visaDetailsDraft: VisaDetailsDraftState;
  onChangeVisaDetailsDraft: (updater: (prev: VisaDetailsDraftState) => VisaDetailsDraftState) => void;

  // timeline
  timeline: TransferTimelineEntry[];
};

/* ------------------------------ Reusable UI ------------------------------- */

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  disabled,
  min,
  step,
}: {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  step?: number;
}) {
  return (
    <input
      value={value as any}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      required={required}
      disabled={disabled}
      min={min}
      step={step}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    />
  );
}

function UiSelect<T extends string>({
  value,
  onChange,
  options,
  placeholder = "— Select —",
  required,
}: {
  value?: T | "";
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: T[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={onChange}
      required={required}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function Field({
  label,
  children,
  hint,
  disabled = false,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`block ${disabled ? "opacity-50" : ""}`}>
      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-700">
        {label}
      </div>
      {children}
      {hint ? (
        <div className="mt-1 text-[11px] text-gray-500">{hint}</div>
      ) : null}
    </label>
  );
}

function Section({
  title,
  icon,
  subtitle,
  children,
}: {
  title: string;
  icon?: ReactNode;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {icon ? <span className="text-sky-600">{icon}</span> : null}
            <span>{title}</span>
          </div>
          {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

/* ----------------------------- Main form cmp ------------------------------ */
const EditLeadForm: React.FC<EditLeadFormProps> = ({
  value,
  onChange,
  errorMessage,
  submitting = false,
  onSubmit,
  onCancel,

  productOptions,
  employeeOptions,
  statusOptions,
  sourceOptions,
  priorityOptions,

  transferProductOptions,
  transferProductId,
  onChangeTransferProductId,
  transferToUserId,
  onChangeTransferToUserId,

  // 🔹 make sure these are here
  transferToProductId,
  onChangeTransferToProductId,

  transferReasonKey,
  onChangeTransferReasonKey,
  transferReasonText,
  onChangeTransferReasonText,
  onTransfer,
  transferring = false,
  canManageTransfers = true,
  isVisaDetailsSaving = false,
  visaLeadProductId,
  onChangeVisaLeadProductId,
  visaProductOptions,
  visaDetailsDraft,
  onChangeVisaDetailsDraft,

  timeline,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const canTransfer =
    canManageTransfers &&
    transferProductOptions.length > 0 &&
    employeeOptions.length > 0;

  // Local modal open/close state (pure UI concern)
  const [showTransferModal, setShowTransferModal] = useState(false);

  const openTransferModal = () => {
    if (!canTransfer || transferring) return;
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    if (transferring) return; // avoid closing mid-request
    setShowTransferModal(false);
  };

  const handleTransferClick = async () => {
    await onTransfer();
    setShowTransferModal(false);
  };

  // Normalized product options to `{ id, name }` for multi-select behaviour
  const allProducts = useMemo(
    () =>
      productOptions.map((o) => ({
        id: Number(o.value),
        name: o.label,
      })),
    [productOptions]
  );

  // These assume your EditLeadFormState has `products: number[]` + `productsOpen: boolean`
  const selectedProducts = useMemo(
    () => allProducts.filter((p) => value.products?.includes(p.id)),
    [allProducts, value.products]
  );

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    []
  );
  const selectedCountryOption = useMemo(
    () =>
      countryOptions.find((c) => c.value === visaDetailsDraft.countryIso2) ??
      null,
    [countryOptions, visaDetailsDraft.countryIso2]
  );

  const canEditDocsReceived = Boolean(
    visaDetailsDraft.enquiryAt || visaDetailsDraft.docsReceivedAt
  );
  const canEditDocsSubmitted = Boolean(
    visaDetailsDraft.docsReceivedAt || visaDetailsDraft.docsSubmittedAt
  );
  const canEditDocsCollected = Boolean(
    visaDetailsDraft.docsSubmittedAt || visaDetailsDraft.docsCollectedAt
  );
  const canEditAppointment = Boolean(
    visaDetailsDraft.docsCollectedAt || visaDetailsDraft.appointmentAt
  );
  const canEditInvoice = true;
  const canEditReceipt = true;

  const toggleProduct = (id: number) => {
    const current = value.products ?? [];
    const exists = current.includes(id);
    const next = exists ? current.filter((x) => x !== id) : [...current, id];

    onChange({ products: next });
  };

  // Group timeline by "product flow" label
  const groupedTimeline = useMemo(() => {
    const map = new Map<string, TransferTimelineEntry[]>();

    for (const t of timeline ?? []) {
      const key =
        t.fromProductName || t.toProductName || "Unknown product";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }

    return Array.from(map.entries()).map(([productName, entries]) => ({
      productName,
      entries,
    }));
  }, [timeline]);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={
          canManageTransfers
            ? "grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)]"
            : "grid grid-cols-1 gap-6"
        }
      >
        <div className="space-y-5">
          {errorMessage ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {errorMessage}
            </div>
          ) : null}

          <Section
            title="Lead Information"
            subtitle="Contact, ownership, pipeline details, and notes in one section."
            icon={<FiLayers className="h-4 w-4" />}
          >
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                {selectedProducts.length} product
                {selectedProducts.length === 1 ? "" : "s"}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                Status: {value.status || "—"}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                Priority: {value.priority || "—"}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="First Name">
                <Input
                  value={value.firstName}
                  onChange={(e) => onChange({ firstName: e.target.value })}
                  placeholder="Enter first name"
                  required
                />
              </Field>

              <Field label="Last Name">
                <Input
                  value={value.lastName}
                  onChange={(e) => onChange({ lastName: e.target.value })}
                  placeholder="Enter last name"
                  required
                />
              </Field>

              <Field label="Mobile Number">
                <PhoneInput
                  country="in"
                  value={value.phone}
                  onChange={(v) => onChange({ phone: `+${v}` })}
                  containerClass="!w-full"
                  inputClass="!h-[40px] !w-full !rounded-lg !border !border-gray-300 !pl-14 !pr-3 !text-sm"
                  buttonClass="!h-[40px] !border-gray-300 !bg-white !rounded-l-lg"
                />
              </Field>

              <Field label="Email ID">
                <div className="relative">
                  <FiMail className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={value.email}
                    onChange={(e) => onChange({ email: e.target.value })}
                    placeholder="name@email.com"
                    required
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>
              </Field>

              <Field label="Date of lead generated">
                <div className="relative">
                  <FiCalendar className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={value.date}
                    onChange={(e) => onChange({ date: e.target.value })}
                    required
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>
              </Field>

              <Field label="Lead Owner">
                <select
                  value={value.assignedToUserId != null ? String(value.assignedToUserId) : ""}
                  onChange={(e) =>
                    onChange({
                      assignedToUserId: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  required
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="" disabled>
                    — Select lead owner —
                  </option>
                  {employeeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="my-4 border-t border-slate-200 pt-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
                Lead Details
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Products" hint="Select one or more products for this lead.">
                    <div className="relative">
                      <button
                        type="button"
                        className="flex min-h-[40px] w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-gray-400"
                        onClick={() => onChange({ productsOpen: !value.productsOpen })}
                        disabled={submitting || transferring}
                      >
                        <span className="flex items-center gap-2 truncate text-gray-700">
                          <FiTag className="h-3.5 w-3.5 text-gray-400" />
                          <span className="truncate">
                            {allProducts.length === 0
                              ? "No products found"
                              : (value.products ?? []).length === 0
                              ? "— Select products —"
                              : selectedProducts.map((p) => p.name).join(", ")}
                          </span>
                        </span>
                        <span className="ml-2 text-xs text-gray-400">▼</span>
                      </button>

                      {value.productsOpen && allProducts.length > 0 && (
                        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow">
                          {allProducts.map((p) => {
                            const selected = (value.products ?? []).includes(p.id);
                            return (
                              <div
                                key={p.id ?? p.name}
                                onClick={() => toggleProduct(p.id)}
                                className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 ${
                                  selected ? "bg-sky-50 text-sky-700" : ""
                                }`}
                              >
                                <span className="truncate">{p.name}</span>
                                {selected && <span className="text-[11px] font-medium">✔</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {selectedProducts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {selectedProducts.map((p) => (
                          <span
                            key={p.id}
                            className="flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700"
                          >
                            {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </Field>
                </div>

                <Field label="Status">
                  <UiSelect
                    value={value.status}
                    onChange={(e) => onChange({ status: e.target.value as LeadStatusUi })}
                    options={statusOptions}
                    required
                  />
                </Field>

                <Field label="Source">
                  <UiSelect
                    value={value.source || ""}
                    onChange={(e) => onChange({ source: e.target.value as LeadSourceUi })}
                    options={sourceOptions}
                    placeholder="— Select source —"
                  />
                </Field>

                <Field label="Priority">
                  <UiSelect
                    value={value.priority || ""}
                    onChange={(e) => onChange({ priority: e.target.value as PriorityUi })}
                    options={priorityOptions}
                    placeholder="— Select priority —"
                  />
                </Field>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
                Internal Notes
              </h3>
            <Field label="Notes">
              <textarea
                value={value.notes || ""}
                onChange={(e) => onChange({ notes: e.target.value })}
                placeholder="Additional notes about this lead (optional)"
                rows={4}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
              />
            </Field>
            </div>
          </Section>

          <div className="sticky bottom-3 z-10 rounded-2xl border border-slate-200 bg-white/95 px-3 py-3 shadow-lg backdrop-blur sm:px-4">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={onCancel}
                disabled={submitting || transferring}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                disabled={submitting || transferring}
              >
                {submitting ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>

        {canManageTransfers ? (
        <div className="space-y-5 lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-4 sm:py-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">
                Lead Movement
              </h2>
              {!canTransfer && (
                <span className="text-[11px] text-gray-500">
                  Add products and users to enable transfers
                </span>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={openTransferModal}
                disabled={!canTransfer || transferring}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                Transfer lead
              </button>
            </div>

            <h3 className="mb-3 mt-4 text-sm font-semibold text-gray-900">
              Transfer history
            </h3>

            {groupedTimeline.length === 0 ? (
              <div className="text-xs text-gray-500">
                No transfers recorded for this lead yet.
              </div>
            ) : (
              <div className="space-y-4">
                {groupedTimeline.map(({ productName, entries }) => (
                  <div key={productName} className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-xs font-semibold text-white">
                          {productName.charAt(0).toUpperCase() || "P"}
                        </span>
                        <div className="text-xs font-semibold text-gray-900">
                          {productName}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {entries.length}{" "}
                        {entries.length === 1 ? "transfer" : "transfers"}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <ol className="space-y-2">
                        {entries.map((t) => {
                          const whenLabel = t.when
                            ? new Date(t.when).toLocaleString()
                            : "Unknown time";

                          return (
                            <li
                              key={t.id}
                              className="text-xs text-gray-700"
                            >
                              {/* details: BEFORE → AFTER layout */}
                              <div className="w-full rounded-lg border border-gray-200 bg-white/80 px-3 py-2">
                                <div className="mb-2 flex items-center gap-1 text-[11px] text-gray-500">
                                  <FiClock className="h-3 w-3" />
                                  <span className="truncate">
                                    {whenLabel}
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                  {/* BEFORE */}
                                  <div>
                                    <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                      From
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      Product
                                    </div>
                                    <div className="mb-0.5 text-xs font-medium text-gray-900">
                                      {t.fromProductName || "—"}
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                      <FiUser className="h-3 w-3" />
                                      <span>
                                        {t.fromUserName || "—"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* AFTER */}
                                  <div>
                                    <div className="mb-0.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                      <span>To</span>
                                      <FiArrowRight className="h-3 w-3 text-gray-400" />
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      Product
                                    </div>
                                    <div className="mb-0.5 text-xs font-medium text-gray-900">
                                      {t.toProductName || "—"}
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                      <FiUser className="h-3 w-3" />
                                      <span>
                                        {t.toUserName || "—"}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {t.reason && (
                                  <div className="mt-1 text-[11px] text-gray-600">
                                    <span className="uppercase text-gray-500">
                                      Reason:
                                    </span>{" "}
                                    {t.reason}
                                  </div>
                                )}

                                {t.actor && (
                                  <div className="mt-1 text-[11px] text-gray-500">
                                    By {t.actor}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {visaProductOptions.length > 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-4 sm:py-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">
              Visa Details
            </h2>

            <div className="space-y-3">
              <Field label="Product line">
                <select
                  value={visaLeadProductId != null ? String(visaLeadProductId) : ""}
                  onChange={(e) =>
                    onChangeVisaLeadProductId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">— Select product line —</option>
                  {visaProductOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Country">
                  <Select
                    options={countryOptions}
                    value={selectedCountryOption}
                    onChange={(
                      option: SingleValue<{ value: string; label: string }>
                    ) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        countryIso2: option?.value ?? "",
                      }))
                    }
                    isSearchable
                    isClearable
                    isDisabled={isVisaDetailsSaving}
                    placeholder="Select country"
                    className="text-sm"
                    classNamePrefix="rs"
                  />
                </Field>

                <Field label="Enquiry">
                  <Input
                    type="date"
                    value={visaDetailsDraft.enquiryAt}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        enquiryAt: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving}
                  />
                </Field>

                <Field label="Doc Received" disabled={isVisaDetailsSaving || !canEditDocsReceived}>
                  <Input
                    type="date"
                    value={visaDetailsDraft.docsReceivedAt}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        docsReceivedAt: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditDocsReceived}
                  />
                </Field>

                <Field label="Doc Submission" disabled={isVisaDetailsSaving || !canEditDocsSubmitted}>
                  <Input
                    type="date"
                    value={visaDetailsDraft.docsSubmittedAt}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        docsSubmittedAt: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditDocsSubmitted}
                  />
                </Field>

                <Field label="Doc Collection" disabled={isVisaDetailsSaving || !canEditDocsCollected}>
                  <Input
                    type="date"
                    value={visaDetailsDraft.docsCollectedAt}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        docsCollectedAt: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditDocsCollected}
                  />
                </Field>
                <Field label="Appointment" disabled={isVisaDetailsSaving || !canEditAppointment}>
                  <Input
                    type="date"
                    value={visaDetailsDraft.appointmentAt}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        appointmentAt: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditAppointment}
                  />
                </Field>

                <Field label="Invoice" disabled={isVisaDetailsSaving || !canEditInvoice}>
                  <Input
                    type="number"
                    step={0.01}
                    min={0}
                    placeholder="Enter amount"
                    value={visaDetailsDraft.invoiceAmount}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        invoiceAmount: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditInvoice}
                  />
                </Field>

                <Field label="Receipt" disabled={isVisaDetailsSaving || !canEditReceipt}>
                  <Input
                    type="number"
                    step={0.01}
                    min={0}
                    placeholder="Enter amount"
                    value={visaDetailsDraft.receiptAmount}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        receiptAmount: e.target.value,
                      }))
                    }
                    disabled={isVisaDetailsSaving || !canEditReceipt}
                  />
                </Field>

                <Field label="Login">
                  <Input
                    value={visaDetailsDraft.loginId}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        loginId: e.target.value,
                      }))
                    }
                    placeholder="Portal login id"
                    disabled={isVisaDetailsSaving}
                  />
                </Field>

                <Field label="Password">
                  <Input
                    value={visaDetailsDraft.loginPassword}
                    onChange={(e) =>
                      onChangeVisaDetailsDraft((prev) => ({
                        ...prev,
                        loginPassword: e.target.value,
                      }))
                    }
                    placeholder="Portal password"
                    disabled={isVisaDetailsSaving}
                  />
                </Field>
              </div>

              <Field label="Remark">
                <textarea
                  value={visaDetailsDraft.remark}
                  onChange={(e) =>
                    onChangeVisaDetailsDraft((prev) => ({
                      ...prev,
                      remark: e.target.value,
                    }))
                  }
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  placeholder="Visa remarks (optional)"
                />
              </Field>

            </div>
          </div>
          ) : null}
        </div>
        ) : null}
      </form>

      {/* =========================
       * TRANSFER MODAL
       * ========================= */}
      {canManageTransfers && showTransferModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-lg sm:p-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Transfer lead
              </h3>
              <button
                type="button"
                onClick={closeTransferModal}
                disabled={transferring}
                className="text-xs text-gray-500 hover:text-gray-800"
              >
                Close
              </button>
            </div>

            <div className="space-y-3">
              <Field label="Product line">
                <select
                  value={
                    transferProductId != null
                      ? String(transferProductId)
                      : ""
                  }
                  onChange={(e) =>
                    onChangeTransferProductId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  disabled={transferring}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="" disabled>
                    — Select product line —
                  </option>
                  {transferProductOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Change product to (optional)"
                hint="Leave empty to keep the same product."
              >
                <select
                  value={
                    transferToProductId != null
                      ? String(transferToProductId)
                      : ""
                  }
                  onChange={(e) =>
                    onChangeTransferToProductId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  disabled={transferring}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">— Keep same product —</option>
                  {productOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Transfer to user">
                <select
                  value={
                    transferToUserId != null
                      ? String(transferToUserId)
                      : ""
                  }
                  onChange={(e) =>
                    onChangeTransferToUserId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  disabled={transferring}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="" disabled>
                    — Select user —
                  </option>
                  {employeeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Reason">
                <select
                  value={transferReasonKey || ""}
                  onChange={(e) => onChangeTransferReasonKey(e.target.value)}
                  disabled={transferring}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">— Select reason (optional) —</option>
                  {TRANSFER_REASON_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Notes (optional)">
                <textarea
                  value={transferReasonText}
                  onChange={(e) => onChangeTransferReasonText(e.target.value)}
                  disabled={transferring}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                             placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  placeholder="Short note about why this lead is being transferred…"
                />
              </Field>

              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeTransferModal}
                  disabled={transferring}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleTransferClick}
                  disabled={transferring}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {transferring ? "Transferring…" : "Confirm transfer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditLeadForm;
